export type CftcRegimeSnapshot = {
  assetKey: string;
  asOfDate: string;
  publishedDate?: string;
  regimeLabel:
    | "Squeeze Watch"
    | "Macro-Led"
    | "Flow-Driven"
    | "Trend-Aligned"
    | "Exhaustion"
    | "Neutral";
  macroBias: "Bullish" | "Bearish" | "Neutral";
  confidence?: "Low" | "Med" | "High";
  notes?: string[];
};

export type ConfluenceBadgeState = {
  label: "MACRO–MICRO ALIGNED" | "COUNTER-MACRO" | "NEUTRAL MACRO" | "MACRO UNAVAILABLE";
  tooltip: string;
  tone: "aligned" | "counter" | "neutral" | "unavailable";
};

const SYMBOL_TO_CFTC_KEY: Record<string, string> = {
  EURUSD: "EURO FX - CHICAGO MERCANTILE EXCHANGE",
  GBPUSD: "BRITISH POUND STERLING - CHICAGO MERCANTILE EXCHANGE",
  USDJPY: "JAPANESE YEN - CHICAGO MERCANTILE EXCHANGE",
  AUDUSD: "AUSTRALIAN DOLLAR - CHICAGO MERCANTILE EXCHANGE",
  NZDUSD: "NEW ZEALAND DOLLAR - CHICAGO MERCANTILE EXCHANGE",
  USDCAD: "CANADIAN DOLLAR - CHICAGO MERCANTILE EXCHANGE",
  USDCHF: "SWISS FRANC - CHICAGO MERCANTILE EXCHANGE",
  USDMXN: "MEXICAN PESO - CHICAGO MERCANTILE EXCHANGE",
  USDBRL: "BRAZILIAN REAL - CHICAGO MERCANTILE EXCHANGE",
};

const normalizeSymbol = (symbol: string) =>
  symbol.replace(/[^a-zA-Z]/g, "").toUpperCase();

export const mapIntradaySymbolToCftcKey = (symbol: string) => {
  const cleaned = normalizeSymbol(symbol);
  return SYMBOL_TO_CFTC_KEY[cleaned] || null;
};

export const computeConfluenceBadge = (
  setupDirection: "LONG" | "SHORT",
  macroBias: "Bullish" | "Bearish" | "Neutral" | null,
  hasSnapshot: boolean
): ConfluenceBadgeState => {
  if (!hasSnapshot || !macroBias) {
    return {
      label: "MACRO UNAVAILABLE",
      tooltip:
        "No CFTC regime snapshot for this asset. Confluence is not evaluated.",
      tone: "unavailable",
    };
  }
  if (macroBias === "Neutral") {
    return {
      label: "NEUTRAL MACRO",
      tooltip:
        "Weekly CFTC macro bias is neutral. Treat this setup on its own merits.",
      tone: "neutral",
    };
  }
  const aligned =
    (macroBias === "Bullish" && setupDirection === "LONG") ||
    (macroBias === "Bearish" && setupDirection === "SHORT");
  return aligned
    ? {
        label: "MACRO–MICRO ALIGNED",
        tooltip:
          "Weekly CFTC regime and intraday structure point in the same direction. This is a context boost, not a signal.",
        tone: "aligned",
      }
    : {
        label: "COUNTER-MACRO",
        tooltip:
          "This setup is counter to the weekly CFTC macro bias. Consider smaller size or faster profit-taking.",
        tone: "counter",
      };
};

export const isSnapshotStale = (asOfDate: string | null, now: number) => {
  if (!asOfDate) return false;
  const parsed = Date.parse(asOfDate);
  if (Number.isNaN(parsed)) return false;
  const days = Math.floor((now - parsed) / (1000 * 60 * 60 * 24));
  return days >= 10;
};
